import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    getIntuneApps, getIntuneApp, getDetectedApps, getDetectedAppDevices,
} from '../api/internal/usersInternalApi';
import {
    Card, PageHead, Button, IconButton, Pill, PillDot, Input, Select, FieldLabel,
    EmptyState, Spinner, SpinnerContainer, Notice, DataTable, TableScroll,
} from '../components/ui';
import {
    FilterBar, Muted, Pager, StatGrid, StatTile, AppLayout, AppRail, AppRow,
    AppIcon, InstallBar, StateChip, MonoCell,
} from '../components/security/security.styled';
import { formatDate } from '../components/security/severity';
import { downloadCsv } from '../components/security/csvDownload';

const PAGE_SIZE = 50;

// Libellés d'état tels qu'Intune les renvoie — espaces et casse compris.
const ETATS = [
    { key: 'Installed', label: 'Installées', tone: 'ok' },
    { key: 'Failed', label: 'Échecs', tone: 'danger' },
    { key: 'Install Pending', label: 'En attente', tone: 'accent' },
    { key: 'Not installed', label: 'Non installées', tone: 'warn' },
    { key: 'Not applicable', label: 'Non applicables', tone: 'neutral' },
];

const INTENT_LABEL = { required: 'Requise', available: 'Disponible', uninstall: 'Désinstallation' };

// Teinte déterministe de la pastille : le même logiciel garde sa couleur d'une
// session à l'autre, ce qui aide à le retrouver dans une liste longue.
function graine(nom) {
    let n = 0;
    for (let i = 0; i < (nom || '').length; i += 1) n = (n + nom.charCodeAt(i)) % 997;
    return n;
}

/* ─────────────── Onglet « Déployées par Intune » ─────────────── */

function PanneauDeploiement({ appId, onClose }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [etat, setEtat] = useState('');
    const [recherche, setRecherche] = useState('');

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setDetail(null);
        // L'état local du panneau repart à zéro d'une application à l'autre :
        // garder un filtre « Échecs » en changeant d'application donnerait un
        // panneau vide qu'on croirait cassé.
        setEtat('');
        setRecherche('');
        getIntuneApp(appId, {}, controller.signal)
            .then((res) => { if (res) setDetail(res); })
            .finally(() => setLoading(false));
        return () => controller.abort();
    }, [appId]);

    const lignes = useMemo(() => {
        if (!detail) return [];
        const terme = recherche.trim().toLowerCase();
        return detail.statuses.filter((s) => {
            if (etat && s.installState !== etat) return false;
            if (!terme) return true;
            return (s.deviceName || '').toLowerCase().includes(terme)
                || (s.userName || '').toLowerCase().includes(terme)
                || (s.userPrincipalName || '').toLowerCase().includes(terme);
        });
    }, [detail, etat, recherche]);

    const exporter = () => {
        if (!detail || lignes.length === 0) return;
        downloadCsv(
            `${detail.application.displayName || 'application'}_deploiement.csv`,
            [
                'Application', 'Appareil', "État de l'installation", "Détail de l'état",
                'Version installée', "Code d'erreur", 'Utilisateur', 'Plateforme', 'Relevé le',
            ],
            lignes.map((s) => [
                detail.application.displayName || '',
                s.deviceName || '',
                s.installState || '',
                s.installStateDetail || '',
                s.appVersion || '',
                s.hexErrorCode || '',
                s.userName || s.userPrincipalName || '',
                s.platform || '',
                formatDate(s.lastModifiedDateTime),
            ])
        );
    };

    if (loading) {
        return <Card><SpinnerContainer><Spinner /></SpinnerContainer></Card>;
    }
    if (!detail) {
        return (
            <Card>
                <Notice $tone="danger"><i className="bx bx-error-circle" />Chargement du détail impossible.</Notice>
            </Card>
        );
    }

    const { application, parEtat } = detail;

    return (
        <Card $pad="16px">
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' }}>
                <AppIcon $seed={graine(application.displayName)}>
                    {(application.displayName || '?').charAt(0)}
                </AppIcon>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700 }}>{application.displayName}</div>
                    <Muted style={{ fontSize: '0.72rem' }}>
                        {application.publisher || 'Éditeur inconnu'} · {application.appType} · {application.platform || 'plateforme inconnue'}
                        {application.version && <> · version {application.version}</>}
                    </Muted>
                </div>
                <IconButton $bordered onClick={onClose} title="Fermer le détail">
                    <i className="bx bx-x" />
                </IconButton>
            </div>

            {/* Le paramétrage d'affectation : c'est lui qui dit à qui
                l'application est destinée, et sous quelle intention. */}
            {application.assignments?.length > 0 && (
                <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    {application.assignments.map((a, i) => (
                        <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'baseline', flexWrap: 'wrap' }}>
                            <Pill $tone={a.targetType === 'exclusionGroupAssignmentTarget' ? 'warn' : a.intent === 'required' ? 'accent' : 'neutral'}>
                                {a.targetType === 'exclusionGroupAssignmentTarget' && <i className="bx bx-block" />}
                                {a.intentLabel || a.intent}
                            </Pill>
                            <Muted style={{ fontSize: '0.75rem' }}>{a.targetLabel}</Muted>
                        </div>
                    ))}
                </div>
            )}

            <FilterBar style={{ marginBottom: '10px' }}>
                <Input
                    style={{ flex: 1, minWidth: '160px' }}
                    placeholder="Appareil ou utilisateur..."
                    value={recherche}
                    onChange={(e) => setRecherche(e.target.value)}
                />
                <Button $variant="secondary" onClick={exporter} disabled={lignes.length === 0}>
                    <i className="bx bxs-download" />
                    Exporter
                </Button>
            </FilterBar>

            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
                {ETATS.filter((e) => parEtat[e.key]).map((e) => (
                    <StateChip
                        key={e.key}
                        type="button"
                        $tone={e.tone}
                        $active={etat === e.key}
                        onClick={() => setEtat(etat === e.key ? '' : e.key)}
                    >
                        <i className="dot" />
                        {e.label} <b>{parEtat[e.key]}</b>
                    </StateChip>
                ))}
            </div>

            {lignes.length === 0 ? (
                <EmptyState>
                    <i className="bx bx-devices" />
                    <h3>Aucun appareil</h3>
                    <p>Aucun appareil ne correspond à ces critères.</p>
                </EmptyState>
            ) : (
                <TableScroll>
                    <DataTable>
                        <thead>
                            <tr>
                                <th>Appareil</th>
                                <th>État</th>
                                <th>Version</th>
                                <th>Utilisateur</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lignes.map((s) => (
                                <tr key={s.deviceIdIntune}>
                                    <td>
                                        {s.deviceName || <Muted>Sans nom</Muted>}
                                        {s.installStateDetail && (
                                            <><br /><Muted style={{ fontSize: '0.7rem' }}>{s.installStateDetail}</Muted></>
                                        )}
                                    </td>
                                    <td>
                                        <Pill $tone={ETATS.find((e) => e.key === s.installState)?.tone || 'neutral'}>
                                            <PillDot />{s.installState || 'Inconnu'}
                                        </Pill>
                                    </td>
                                    <td><Muted>{s.appVersion || '—'}</Muted></td>
                                    <td><Muted>{s.userName || s.userPrincipalName || '—'}</Muted></td>
                                </tr>
                            ))}
                        </tbody>
                    </DataTable>
                </TableScroll>
            )}
        </Card>
    );
}

function OngletDeploiement() {
    const [searchParams, setSearchParams] = useSearchParams();
    const selection = searchParams.get('app');

    const [search, setSearch] = useState('');
    const [appType, setAppType] = useState('');
    const [onlyFailures, setOnlyFailures] = useState(false);
    const [page, setPage] = useState(1);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);

    const charger = useCallback((signal) => {
        setLoading(true);
        return getIntuneApps({
            page, limit: PAGE_SIZE, search, appType,
            onlyFailures: onlyFailures ? 'true' : undefined,
        }, signal)
            .then((data) => { if (data) setResult(data); })
            .finally(() => setLoading(false));
    }, [page, search, appType, onlyFailures]);

    useEffect(() => {
        const controller = new AbortController();
        const timer = setTimeout(() => charger(controller.signal), search ? 300 : 0);
        return () => { clearTimeout(timer); controller.abort(); };
    }, [charger, search]);

    const majFiltre = (setter) => (valeur) => { setter(valeur); setPage(1); };
    const pagination = result?.pagination;

    // Les compteurs du bandeau viennent de la PAGE COURANTE et le disent : un
    // total sur tout le catalogue demanderait une agrégation serveur, et un
    // chiffre dont on ne sait pas ce qu'il couvre ne vaut rien.
    const surLaPage = result?.data || [];
    const totalEchecs = surLaPage.reduce((n, a) => n + (a.failedDeviceCount || 0), 0);
    const editeurs = new Set(surLaPage.map((a) => a.publisher).filter(Boolean)).size;

    return (
        <>
            <Card>
                <FilterBar>
                    <div style={{ flex: 1, minWidth: '220px' }}>
                        <FieldLabel htmlFor="app-search">Recherche</FieldLabel>
                        <Input
                            id="app-search"
                            style={{ width: '100%', marginTop: '6px' }}
                            placeholder="Nom d'application ou éditeur..."
                            value={search}
                            onChange={(e) => majFiltre(setSearch)(e.target.value)}
                        />
                    </div>
                    <div>
                        <FieldLabel htmlFor="app-type">Type</FieldLabel>
                        <Select
                            id="app-type"
                            style={{ marginTop: '6px' }}
                            value={appType}
                            onChange={(e) => majFiltre(setAppType)(e.target.value)}
                        >
                            <option value="">Tous</option>
                            <option value="win32LobApp">Win32 (LOB)</option>
                            <option value="winGetApp">WinGet</option>
                            <option value="officeSuiteApp">Suite Office</option>
                            <option value="androidManagedStoreApp">Android Managed Store</option>
                            <option value="iosStoreApp">iOS Store</option>
                            <option value="iosVppApp">iOS VPP</option>
                            <option value="macOsVppApp">macOS VPP</option>
                            <option value="macOSPkgApp">macOS PKG</option>
                        </Select>
                    </div>
                    <label htmlFor="app-failures">
                        <input
                            id="app-failures"
                            type="checkbox"
                            checked={onlyFailures}
                            onChange={(e) => majFiltre(setOnlyFailures)(e.target.checked)}
                        />
                        Avec échecs uniquement
                    </label>
                </FilterBar>
            </Card>

            <StatGrid>
                <StatTile $tone="accent">
                    <div className="label">Applications</div>
                    <div className="value">{pagination?.totalCount?.toLocaleString('fr-FR') ?? '—'}</div>
                    <div className="hint">publiées dans Intune</div>
                </StatTile>
                <StatTile $tone={totalEchecs > 0 ? 'danger' : 'ok'}>
                    <div className="label">Échecs</div>
                    <div className="value">{totalEchecs.toLocaleString('fr-FR')}</div>
                    <div className="hint">déploiements en échec sur cette page</div>
                </StatTile>
                <StatTile $tone="accent">
                    <div className="label">Éditeurs</div>
                    <div className="value">{editeurs}</div>
                    <div className="hint">distincts sur cette page</div>
                </StatTile>
            </StatGrid>

            <AppLayout>
                <Card $pad="14px">
                    {loading && !result ? (
                        <SpinnerContainer><Spinner /></SpinnerContainer>
                    ) : surLaPage.length === 0 ? (
                        <EmptyState>
                            <i className="bx bx-package" />
                            <h3>Aucune application</h3>
                            <p>Aucune application ne correspond à ces critères.</p>
                        </EmptyState>
                    ) : (
                        <>
                            {surLaPage.map((app) => {
                                const cible = (app.installedDeviceCount || 0) + (app.failedDeviceCount || 0)
                                    + (app.pendingInstallDeviceCount || 0) + (app.notInstalledDeviceCount || 0);
                                const part = (n) => (cible > 0 ? `${((n / cible) * 100).toFixed(1)}%` : '0%');
                                return (
                                    <AppRow
                                        key={app.appId}
                                        $selected={selection === app.appId}
                                        onClick={() => setSearchParams(selection === app.appId ? {} : { app: app.appId })}
                                    >
                                        <AppIcon $seed={graine(app.displayName)}>
                                            {(app.displayName || '?').charAt(0)}
                                        </AppIcon>
                                        <div className="body">
                                            <div className="name">{app.displayName || 'Sans nom'}</div>
                                            <div className="meta">
                                                <span>{app.publisher || 'Éditeur inconnu'}</span>
                                                <span>{app.platform || app.appType}</span>
                                                {app.intent && <span>{INTENT_LABEL[app.intent] || app.intent}</span>}
                                                {app.failedDeviceCount > 0 && (
                                                    <span style={{ fontWeight: 600 }}>{app.failedDeviceCount} en échec</span>
                                                )}
                                            </div>
                                            {cible > 0 && (
                                                <InstallBar title={`${app.installedDeviceCount} installées, ${app.failedDeviceCount} en échec, ${app.pendingInstallDeviceCount} en attente, ${app.notInstalledDeviceCount} non installées`}>
                                                    <span className="installed" style={{ width: part(app.installedDeviceCount) }} />
                                                    <span className="failed" style={{ width: part(app.failedDeviceCount) }} />
                                                    <span className="pending" style={{ width: part(app.pendingInstallDeviceCount) }} />
                                                    <span className="notInstalled" style={{ width: part(app.notInstalledDeviceCount) }} />
                                                </InstallBar>
                                            )}
                                        </div>
                                        <div className="count">
                                            <b>{cible.toLocaleString('fr-FR')}</b>
                                            <span>appareils</span>
                                        </div>
                                    </AppRow>
                                );
                            })}

                            {pagination && (
                                <Pager>
                                    <span><strong>{pagination.totalCount.toLocaleString('fr-FR')}</strong> applications</span>
                                    <div className="spacer" />
                                    <span>Page <strong>{pagination.page}</strong> / {pagination.totalPages}</span>
                                    <IconButton $bordered disabled={!pagination.hasPrevPage || loading} onClick={() => setPage((p) => p - 1)} title="Page précédente">
                                        <i className="bx bx-chevron-left" />
                                    </IconButton>
                                    <IconButton $bordered disabled={!pagination.hasNextPage || loading} onClick={() => setPage((p) => p + 1)} title="Page suivante">
                                        <i className="bx bx-chevron-right" />
                                    </IconButton>
                                </Pager>
                            )}
                        </>
                    )}
                </Card>

                {selection && (
                    <AppRail>
                        <PanneauDeploiement appId={selection} onClose={() => setSearchParams({})} />
                    </AppRail>
                )}
            </AppLayout>
        </>
    );
}

/* ─────────────── Onglet « Constatées sur le parc » ─────────────── */

function PanneauConstatee({ detectedAppId, onClose }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [recherche, setRecherche] = useState('');

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setDetail(null);
        setRecherche('');
        getDetectedAppDevices(detectedAppId, controller.signal)
            .then((res) => { if (res) setDetail(res); })
            .finally(() => setLoading(false));
        return () => controller.abort();
    }, [detectedAppId]);

    if (loading) return <Card><SpinnerContainer><Spinner /></SpinnerContainer></Card>;
    if (!detail) {
        return <Card><Notice $tone="danger"><i className="bx bx-error-circle" />Chargement impossible.</Notice></Card>;
    }

    const terme = recherche.trim().toLowerCase();
    const postes = detail.devices.filter((d) => !terme || (d.deviceName || '').toLowerCase().includes(terme));

    const exporter = () => {
        if (postes.length === 0) return;
        downloadCsv(
            `${detail.application.displayName || 'application'}_postes.csv`,
            ['Application', 'Version', 'Nom du poste', 'Device Id Intune'],
            postes.map((d) => [
                detail.application.displayName || '',
                detail.application.version || '',
                d.deviceName || '',
                d.deviceIdIntune || '',
            ])
        );
    };

    return (
        <Card $pad="16px">
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' }}>
                <AppIcon $seed={graine(detail.application.displayName)}>
                    {(detail.application.displayName || '?').charAt(0)}
                </AppIcon>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700 }}>{detail.application.displayName}</div>
                    <Muted style={{ fontSize: '0.72rem' }}>
                        version {detail.application.version || 'inconnue'} · {detail.application.publisher || 'éditeur inconnu'} · {detail.application.platform}
                    </Muted>
                </div>
                <IconButton $bordered onClick={onClose} title="Fermer le détail">
                    <i className="bx bx-x" />
                </IconButton>
            </div>

            <FilterBar style={{ marginBottom: '12px' }}>
                <Input
                    style={{ flex: 1, minWidth: '160px' }}
                    placeholder="Nom de poste..."
                    value={recherche}
                    onChange={(e) => setRecherche(e.target.value)}
                />
                <Button $variant="secondary" onClick={exporter} disabled={postes.length === 0}>
                    <i className="bx bxs-download" />
                    Exporter
                </Button>
            </FilterBar>

            <TableScroll>
                <DataTable>
                    <thead><tr><th>Poste</th><th>Version</th></tr></thead>
                    <tbody>
                        {postes.map((d) => (
                            <tr key={d.deviceIdIntune}>
                                <td>{d.deviceName || <Muted>Sans nom</Muted>}</td>
                                <td><MonoCell>{d.version || '—'}</MonoCell></td>
                            </tr>
                        ))}
                    </tbody>
                </DataTable>
            </TableScroll>
        </Card>
    );
}

function OngletConstatees() {
    const [searchParams, setSearchParams] = useSearchParams();
    const selection = searchParams.get('detected');

    const [search, setSearch] = useState('');
    const [platform, setPlatform] = useState('');
    const [page, setPage] = useState(1);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);

    const charger = useCallback((signal) => {
        setLoading(true);
        return getDetectedApps({ page, limit: PAGE_SIZE, search, platform }, signal)
            .then((data) => { if (data) setResult(data); })
            .finally(() => setLoading(false));
    }, [page, search, platform]);

    useEffect(() => {
        const controller = new AbortController();
        const timer = setTimeout(() => charger(controller.signal), search ? 300 : 0);
        return () => { clearTimeout(timer); controller.abort(); };
    }, [charger, search]);

    const majFiltre = (setter) => (valeur) => { setter(valeur); setPage(1); };
    const pagination = result?.pagination;
    const liste = result?.data || [];

    return (
        <>
            <Card>
                <FilterBar>
                    <div style={{ flex: 1, minWidth: '220px' }}>
                        <FieldLabel htmlFor="det-search">Recherche</FieldLabel>
                        <Input
                            id="det-search"
                            style={{ width: '100%', marginTop: '6px' }}
                            placeholder="Nom de logiciel ou éditeur..."
                            value={search}
                            onChange={(e) => majFiltre(setSearch)(e.target.value)}
                        />
                    </div>
                    <div>
                        <FieldLabel htmlFor="det-platform">Plateforme</FieldLabel>
                        <Select
                            id="det-platform"
                            style={{ marginTop: '6px' }}
                            value={platform}
                            onChange={(e) => majFiltre(setPlatform)(e.target.value)}
                        >
                            <option value="">Toutes</option>
                            {(result?.platforms || []).map((p) => (
                                <option key={p.platform} value={p.platform}>
                                    {p.platform || 'inconnue'} ({p.count})
                                </option>
                            ))}
                        </Select>
                    </div>
                </FilterBar>

                <Notice $tone="accent" style={{ marginTop: '12px' }}>
                    <i className="bx bx-info-circle" />
                    <span>
                        Intune indexe sur le couple <strong>nom + version</strong> : « 7-Zip 26.02 » et
                        « 7-Zip 24.09 » comptent pour deux entrées. C'est ce qui rend visible un parc
                        qui traîne plusieurs versions d'un même logiciel, mais le total s'en trouve
                        élevé. Inventaire collecté une fois par nuit.
                    </span>
                </Notice>
            </Card>

            <AppLayout>
                <Card $pad="14px">
                    {loading && !result ? (
                        <SpinnerContainer><Spinner /></SpinnerContainer>
                    ) : liste.length === 0 ? (
                        <EmptyState>
                            <i className="bx bx-package" />
                            <h3>Aucun logiciel</h3>
                            <p>Aucun logiciel constaté ne correspond à ces critères.</p>
                        </EmptyState>
                    ) : (
                        <>
                            {liste.map((app) => (
                                <AppRow
                                    key={app.detectedAppId}
                                    $selected={selection === app.detectedAppId}
                                    onClick={() => setSearchParams(selection === app.detectedAppId ? {} : { detected: app.detectedAppId })}
                                >
                                    <AppIcon $seed={graine(app.displayName)}>
                                        {(app.displayName || '?').charAt(0)}
                                    </AppIcon>
                                    <div className="body">
                                        <div className="name">{app.displayName || 'Sans nom'}</div>
                                        <div className="meta">
                                            <span>version {app.version || 'inconnue'}</span>
                                            <span>{app.publisher || 'Éditeur inconnu'}</span>
                                            <span>{app.platform}</span>
                                        </div>
                                    </div>
                                    <div className="count">
                                        <b>{(app.deviceCount || 0).toLocaleString('fr-FR')}</b>
                                        <span>postes</span>
                                    </div>
                                </AppRow>
                            ))}

                            {pagination && (
                                <Pager>
                                    <span><strong>{pagination.totalCount.toLocaleString('fr-FR')}</strong> logiciels</span>
                                    <div className="spacer" />
                                    <span>Page <strong>{pagination.page}</strong> / {pagination.totalPages}</span>
                                    <IconButton $bordered disabled={!pagination.hasPrevPage || loading} onClick={() => setPage((p) => p - 1)} title="Page précédente">
                                        <i className="bx bx-chevron-left" />
                                    </IconButton>
                                    <IconButton $bordered disabled={!pagination.hasNextPage || loading} onClick={() => setPage((p) => p + 1)} title="Page suivante">
                                        <i className="bx bx-chevron-right" />
                                    </IconButton>
                                </Pager>
                            )}
                        </>
                    )}
                </Card>

                {selection && (
                    <AppRail>
                        <PanneauConstatee detectedAppId={selection} onClose={() => setSearchParams({})} />
                    </AppRail>
                )}
            </AppLayout>
        </>
    );
}

/* ─────────────── Page ─────────────── */

/**
 * Applications du parc, en deux inventaires distincts.
 *
 * Ce qu'Intune DÉPLOIE et ce qui est CONSTATÉ sur les postes ne sont pas deux
 * vues de la même chose : une application peut être installée sans passer par
 * Intune, et déployée sans jamais s'installer. Les deux ne partagent aucun
 * identifiant — un GUID d'un côté, un condensé de (nom, version) de l'autre —
 * donc rien ne les rapproche automatiquement, et l'onglet est le seul choix
 * honnête plutôt qu'une liste fusionnée qui laisserait croire à un lien.
 */
export default function SecurityApplications() {
    const [onglet, setOnglet] = useState('deployees');

    return (
        <>
            <PageHead>
                <div>
                    <h2>Applications</h2>
                    <p>
                        Ce qu'Intune déploie sur le parc, et ce qui y est réellement installé.
                        Cliquez sur une application pour voir les postes concernés.
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Button
                        $variant={onglet === 'deployees' ? 'primary' : 'secondary'}
                        onClick={() => setOnglet('deployees')}
                    >
                        <i className="bx bx-cloud-download" />
                        Déployées par Intune
                    </Button>
                    <Button
                        $variant={onglet === 'constatees' ? 'primary' : 'secondary'}
                        onClick={() => setOnglet('constatees')}
                    >
                        <i className="bx bx-search-alt" />
                        Découvertes sur le parc
                    </Button>
                </div>
            </PageHead>

            {onglet === 'deployees' ? <OngletDeploiement /> : <OngletConstatees />}
        </>
    );
}
